/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import {
    ARCHIVE_ERROR_NOTIFICATION_DURATION,
    extractServerErrorMessage,
    isRequestTimeout,
    uploadArchiveToSignedUrl
} from '@/utils/api/importArchive';
import { triggerDirectDownload } from '@/utils';
import { designerV2API } from '@/services/designer/designerV2Client';
import { PROJECT_QUERY_KEYS } from '@/services/designer/projects/projects.queries';
import {
    finishProgressNotification,
    showProgressNotification
} from '@/utils/ui';

type TExportCustomerProjectsVariables = {
    isBackup: boolean;
};

export const useExportCustomerProjectsMutation = () => {
    return useMutation<
        { downloadUrl: string },
        Error,
        TExportCustomerProjectsVariables,
        { notificationId: string; isBackup: boolean }
    >({
        mutationKey: ['customer-projects', 'export'],
        mutationFn: async () => {
            const dto = await designerV2API.createCustomerProjectsExportUrl();
            return { downloadUrl: dto.download_url };
        },
        onMutate: ({ isBackup }) => {
            const notificationId = 'export-customer-projects';
            showProgressNotification(
                notificationId,
                isBackup ? 'Backing up projects' : 'Exporting projects',
                isBackup
                    ? 'Exporting current projects before import…'
                    : 'Preparing archive…'
            );
            return { notificationId, isBackup };
        },
        onSuccess: ({ downloadUrl }, _vars, ctx) => {
            const isBackup = ctx?.isBackup ?? false;
            triggerDirectDownload(
                downloadUrl,
                `${isBackup ? 'backup_' : ''}newo-customer-export-${DateTime.now().toFormat('yyyy-LL-dd-HH-mm-ss')}.zip`
            );
            if (ctx?.notificationId) {
                finishProgressNotification(
                    ctx.notificationId,
                    'green',
                    isBackup ? 'Backup downloaded' : 'Projects exported'
                );
            }
        },
        onError: (_error, _vars, ctx) => {
            if (ctx?.notificationId) {
                finishProgressNotification(
                    ctx.notificationId,
                    'red',
                    ctx.isBackup
                        ? 'Could not back up projects'
                        : 'Could not export projects',
                    ctx.isBackup
                        ? 'Import was cancelled. Please try again.'
                        : 'Please try again.'
                );
            }
        }
    });
};

export const useImportCustomerProjectsMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ['customer-projects', 'import'],
        mutationFn: async (archive: File) => {
            const dto = await designerV2API.createCustomerProjectsImportUrl();
            await uploadArchiveToSignedUrl(
                archive,
                dto.upload_url,
                dto.method,
                dto.headers
            );
            return designerV2API.completeCustomerProjectsImport({
                object_name: dto.object_name
            });
        },
        onMutate: (archive) => {
            const notificationId = `import-customer-projects-${archive.name}-${archive.size}`;
            showProgressNotification(
                notificationId,
                'Importing projects',
                `Uploading ${archive.name}…`
            );
            return { notificationId };
        },
        onSuccess: (_result, _vars, ctx) => {
            if (ctx?.notificationId) {
                finishProgressNotification(
                    ctx.notificationId,
                    'green',
                    'Projects imported'
                );
            }
        },
        onError: (error, _vars, ctx) => {
            const detail = extractServerErrorMessage(error);

            const message =
                detail ??
                (isRequestTimeout(error)
                    ? 'Upload timed out. Try a smaller archive or check your connection.'
                    : 'Archive was rejected. Please try a different file.');
            if (ctx?.notificationId) {
                finishProgressNotification(
                    ctx.notificationId,
                    'red',
                    'Could not import projects',
                    message,
                    ARCHIVE_ERROR_NOTIFICATION_DURATION
                );
            }
        },
        onSettled: () => {
            void queryClient.invalidateQueries({
                queryKey: PROJECT_QUERY_KEYS.list(true)
            });
        }
    });
};
